import { jwtDecode } from "jwt-decode";
import TransientAuthTokenError from "@/types/errors/TransientAuthTokenError";
import { adminUploadScopeMismatch, readCurrentAdminUploadScope } from "./adminUploadAuthScope";
import type { FileUploadSession } from "@/db/fileUploadDb";
import type api from "@/api";

type UploadApi = typeof api & { _fetch(url: string, options: RequestInit): Promise<Response> };

/** Bind the actual bearer at dispatch, after buildHeaders has awaited token acquisition. */
export function scopedAdminUploadApi(client: typeof api, session: Partial<Pick<FileUploadSession, "id" | "uploadId" | "batchOrganizationId" | "batchUserId">>): UploadApi {
	const transport = client as UploadApi;
	if (!session.batchOrganizationId && !session.batchUserId) return transport;
	const generation = readCurrentAdminUploadScope()?.generation;
	const scoped = Object.create(client) as UploadApi;
	// The facade fetcher closes over its creator. Never inherit the global client's cached fetcher.
	Object.defineProperty(scoped, "_uploadsClient", { value: null, writable: true });
	scoped._fetch = async (url: string, options: RequestInit) => {
		const scope = readCurrentAdminUploadScope();
		const authorization = new Headers(options.headers).get("authorization");
		let claims: { organization_id?: string; user_id?: string } | undefined;
		try {
			if (authorization?.startsWith("Bearer ")) claims = jwtDecode(authorization.slice(7));
		} catch {
			/* An unreadable bearer cannot establish the upload's identity. */
		}
		if (
			!scope ||
			scope.generation !== generation ||
			adminUploadScopeMismatch(session, scope) ||
			!claims ||
			adminUploadScopeMismatch(session, {
				organizationId: claims.organization_id ?? null,
				userId: claims.user_id ?? null
			})
		) {
			console.warn("[Upload] Auth identity changed before upload request dispatch; retaining the pending source", {
				sessionId: session.id
			});
			throw new TransientAuthTokenError("Upload deferred until its original account is active.");
		}
		return transport._fetch.call(scoped, url, options);
	};
	return scoped;
}
